"use client";

import { useEffect, useState } from "react";
type Region = {
  region: number;
  lat: number;
  lon: number;
  location: string;
  mag: string;
  spot: string;
  area: number;
  spots: number;
};
type SortKey = "area" | "risk" | "pos";
type Status = {
  color: string;
  label: string;
};
const SWPC_URL = "https://services.swpc.noaa.gov/json/solar_regions.json";
const CX = 200;
const CY = 200;
const R = 175;
const GREEN = "#4ade80";
const RED = "#f87171";
function classColor(mag: string): string {
  const c = magCode(mag);
  if (c.includes("D")) return "var(--destructive)"; // δ present → red (flare-dangerous)
  if (c.includes("G")) return "var(--flare-yellow)"; // β-γ → yellow (matches SWL)
  if (c.includes("B")) return "var(--photon-blue)"; // β → calm
  return "var(--muted-foreground)"; // α / none → grey
}
function riskTier(mag: string): number {
  const c = magCode(mag);
  if (c.includes("D")) return 3;
  if (c.includes("G")) return 2;
  if (c.includes("B")) return 1;
  return 0;
}

// Normalize SWPC magnetic class to a canonical code: "A" | "B" | "BG" | "BGD" | "BD" | "GD" | "G" | ""
// Handles both the abbreviated mag_class ("BG") and full mag_string ("Beta-Gamma").
function magCode(mag: string): string {
  const raw = (mag || "").trim();
  if (!raw) return "";
  const s = raw.toLowerCase();
  if (/alpha|beta|gamma|delta/.test(s)) {
    let c = "";
    if (s.includes("alpha")) c += "A";
    if (s.includes("beta")) c += "B";
    if (s.includes("gamma")) c += "G";
    if (s.includes("delta")) c += "D";
    return c;
  }
  return raw.toUpperCase().replace(/[^ABGD]/g, ""); // abbreviated form
}
function parseLocation(loc: string): {
  lat: number;
  lon: number;
} | null {
  const m = (loc || "").match(/([NS])(\d+)([EW])(\d+)/);
  if (!m) return null;
  const lat = (m[1] === "S" ? -1 : 1) * Number(m[2]);
  const lon = (m[3] === "E" ? -1 : 1) * Number(m[4]); // E = left, W = right
  return {
    lat,
    lon
  };
}
function project(lat: number, lon: number) {
  const la = lat * Math.PI / 180;
  const lo = lon * Math.PI / 180;
  return {
    px: CX + Math.cos(la) * Math.sin(lo) * R,
    py: CY - Math.sin(la) * R
  };
}

// mu = cos(lat) * cos(lon): 1 facing us head-on, 0 at the limb
function visibility(lat: number, lon: number): number {
  const mu = Math.cos(lat * Math.PI / 180) * Math.cos(lon * Math.PI / 180);
  return Math.max(0, mu);
}
function dotRadius(area: number): number {
  return Math.max(4, Math.min(13, 4 + Math.sqrt(area || 0) / 3.2));
}
const SORTERS: Record<SortKey, (a: Region, b: Region) => number> = {
  area: (a, b) => b.area - a.area || a.region - b.region,
  risk: (a, b) => riskTier(b.mag) - riskTier(a.mag) || b.area - a.area,
  pos: (a, b) => a.lon - b.lon
};
const SORT_LABELS: {
  key: SortKey;
  label: string;
}[] = [{
  key: "area",
  label: "AREA"
}, {
  key: "risk",
  label: "FLARE RISK"
}, {
  key: "pos",
  label: "E → W"
}];

// Telemetry-matched card (mirrors the Instrument shield on the homepage)
function StatCard({
  label,
  value,
  unit,
  status,
  detail
}: {
  label: string;
  value: string | number;
  unit?: string;
  status?: Status;
  detail?: string;
}) {
  return <div className="flex flex-col gap-3 border border-secondary/20 rounded-md p-5 md:p-6" style={{
    backgroundColor: "rgba(15, 17, 21, 0.6)",
    backdropFilter: "blur(24px)"
  }}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/40">{label}</span>
        {status && <span className="size-1.5 animate-pulse" style={{
        backgroundColor: status.color
      }} />}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-default font-bold text-3xl md:text-4xl text-foreground tabular-nums leading-none">{value}</span>
        {unit && <span className="font-mono text-xs text-foreground/50 uppercase">{unit}</span>}
      </div>
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
        {status && <span style={{
        color: status.color
      }}>{status.label}</span>}
        {detail && <span className="text-foreground/40">{detail}</span>}
      </div>
    </div>;
}
function ReadingTheDisk() {
  return <div className="border-l-2 border-[var(--corona-orange)]/60 bg-card/20 rounded-r-md pl-5 pr-5 py-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--corona-orange)] mb-3">READING THE DISK</p>
      <ul className="space-y-2.5 font-serif text-[14px] leading-relaxed text-muted-foreground">
        <li><span className="text-foreground">Orientation —</span> north is up, east is left, west is right, the same view you'd get at the eyepiece. Regions drift left to right over about two weeks as the Sun rotates.</li>
        <li><span className="text-foreground">Each dot is an active region</span> — a sunspot group. A bigger dot means a larger spot area.</li>
        <li><span className="text-foreground">Brightness is placement —</span> bright dots face us head-on and are well-placed to image; dim dots sit near the limb, foreshortened and about to rotate off.</li>
        <li><span className="text-foreground">Colour is magnetic complexity,</span> the simplest predictor of flares. Grey and blue are calm; orange and red can erupt. A red (β-γ-δ) region is the one to keep an eye on during an imaging session.</li>
        <li><span className="text-foreground">Tap any region</span> — on the disk or in the list — for its full record.</li>
      </ul>
    </div>;
}
export default function SolarDiskPlot() {
  const [regions, setRegions] = useState<Region[]>([]);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [observedDate, setObservedDate] = useState<string>("");
  const [active, setActive] = useState<number | null>(null);
  const [sort, setSort] = useState<SortKey>("area");
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const res = await fetch(SWPC_URL, {
          cache: "no-store"
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw: any[] = await res.json();
        if (!Array.isArray(raw) || raw.length === 0) throw new Error("empty");
        const latest = raw.reduce((acc, r) => r.observed_date > acc ? r.observed_date : acc, raw[0].observed_date);
        const parsed: Region[] = raw.filter(r => r.observed_date === latest).map(r => {
          const p = parseLocation(r.location);
          let lat: number, lon: number;
          if (p) {
            lat = p.lat;
            lon = p.lon;
          } else {
            lat = Number(r.latitude);
            lon = Number(r.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
          }
          return {
            region: Number(r.region),
            lat,
            lon,
            location: r.location || "",
            mag: r.mag_string || r.mag_class || "",
            spot: r.spot_class || "",
            area: Number(r.area) || 0,
            spots: Number(r.number_spots) || 0
          } as Region;
        }).filter((r): r is Region => r !== null).sort(SORTERS.area);
        if (!alive) return;
        setRegions(parsed);
        setObservedDate(latest ? String(latest).slice(0, 10) : "");
        setStatus("ok");
      } catch {
        if (alive) setStatus("error");
      }
    }
    load();
    const id = setInterval(load, 30 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);
  const activeRegion = regions.find(r => r.region === active) || null;
  const deltaCount = regions.filter(r => magCode(r.mag).includes("D")).length;
  const spottedCount = regions.filter(r => r.area > 0).length;
  const largest = regions.reduce<Region | null>((m, r) => r.area > (m?.area ?? -1) ? r : m, null);
  const sortedRegions = [...regions].sort(SORTERS[sort]);
  const toggle = (n: number) => setActive(cur => cur === n ? null : n);
  const pad = (n: number) => String(n).padStart(2, "0");
  return <section data-section-id="solar-disk" id="solar-disk" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-border relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-[var(--corona-orange)]/5 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-[var(--corona-orange)]" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--corona-orange)]">
              LIVE FROM SWPC · ACTIVE REGIONS
            </span>
          </div>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-foreground">
            The Disk Today
          </h2>
        </div>

        {/* Stat strip — telemetry visual language */}
        {status === "ok" && <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-12">
            <StatCard label="NUMBERED" value={pad(regions.length)} status={{
          color: GREEN,
          label: "ON DISK"
        }} detail="NOAA SWPC" />
            <StatCard label="SPOTTED" value={pad(spottedCount)} status={{
          color: GREEN,
          label: "WITH SPOTS"
        }} detail={`OF ${regions.length}`} />
            <StatCard label="FLARE-CAPABLE" value={pad(deltaCount)} status={deltaCount > 0 ? {
          color: RED,
          label: "WATCH"
        } : {
          color: GREEN,
          label: "NONE"
        }} detail="β-γ-δ" />
            <StatCard label="LARGEST" value={largest && largest.area > 0 ? largest.area : "—"} unit={largest && largest.area > 0 ? "MSH" : undefined} status={{
          color: GREEN,
          label: "PEAK"
        }} detail={largest && largest.area > 0 ? `AR${largest.region}` : "NO SPOTS"} />
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Disk */}
          <div className="lg:col-span-7">
            <svg viewBox="0 0 400 400" className="w-full max-w-[560px] mx-auto block" role="img" aria-label="Solar disk with active regions, north up, east left, west right">
           <defs>
                <style>{`
                  .sdp-spin { transform-box: fill-box; transform-origin: center; animation: sdpSpin 1000s linear infinite; }
                  .sdp-breathe { animation: sdpBreathe 9s ease-in-out infinite; }
                  .sdp-limb { animation: sdpLimb 7s ease-in-out infinite; }
                  @keyframes sdpSpin { to { transform: rotate(360deg); } }
                  @keyframes sdpBreathe { 0%, 100% { opacity: .12; } 50% { opacity: .20; } }
                  @keyframes sdpLimb { 0%, 100% { opacity: .9; } 50% { opacity: 1; } }
                  @media (prefers-reduced-motion: reduce) {
                    .sdp-spin, .sdp-breathe, .sdp-limb { animation: none; }
                  }
                `}</style>
                <radialGradient id="solarLimb" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#FFA94D" stopOpacity={0.16} />
                  <stop offset="55%" stopColor="#F97316" stopOpacity={0.07} />
                  <stop offset="100%" stopColor="#F97316" stopOpacity={0} />
                </radialGradient>
                <filter id="sdpChromo" x="-20%" y="-20%" width="140%" height="140%">
                  <feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves={4} seed={11} stitchTiles="stitch" result="n" />
                  <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.96  0 0 0 0 0.52  0 0 0 0 0.16  0.6 0 0 0 0" />
                </filter>
                <clipPath id="sdpDisk"><circle cx={CX} cy={CY} r={R} /></clipPath>
              </defs>

              <circle cx={CX} cy={CY} r={R} fill="url(#solarLimb)" className="sdp-limb" />
              <g clipPath="url(#sdpDisk)">
                <g className="sdp-breathe">
                  <g className="sdp-spin">
                    <rect x={0} y={0} width={400} height={400} filter="url(#sdpChromo)" />
                  </g>
                </g>
              </g>
              <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--border)" strokeWidth={1.5} />
              <line x1={CX - R} y1={CY} x2={CX + R} y2={CY} stroke="var(--border)" strokeOpacity={0.4} strokeWidth={1} />
              <line x1={CX} y1={CY - R} x2={CX} y2={CY + R} stroke="var(--border)" strokeOpacity={0.4} strokeWidth={1} />
              <ellipse cx={CX} cy={CY} rx={R / 2} ry={R} fill="none" stroke="var(--border)" strokeOpacity={0.18} strokeWidth={1} />
              <ellipse cx={CX} cy={CY} rx={R} ry={R / 2} fill="none" stroke="var(--border)" strokeOpacity={0.18} strokeWidth={1} />
              <text x={CX} y={CY - R - 8} textAnchor="middle" className="font-mono" fontSize={11} fill="var(--muted-foreground)">N</text>
              <text x={CX} y={CY + R + 16} textAnchor="middle" className="font-mono" fontSize={11} fill="var(--muted-foreground)">S</text>
              <text x={CX - R - 10} y={CY + 4} textAnchor="middle" className="font-mono" fontSize={11} fill="var(--muted-foreground)">E</text>
              <text x={CX + R + 10} y={CY + 4} textAnchor="middle" className="font-mono" fontSize={11} fill="var(--muted-foreground)">W</text>

              {regions.map(r => {
              const {
                px,
                py
              } = project(r.lat, r.lon);
              const c = classColor(r.mag);
              const rr = dotRadius(r.area);
              const isActive = active === r.region;
              const isDelta = magCode(r.mag).includes("D");
              const showLabel = isActive || isDelta;
              const groupOpacity = isActive ? 1 : 0.3 + 0.7 * visibility(r.lat, r.lon);
              return <g key={r.region} onClick={() => toggle(r.region)} style={{
                cursor: "pointer"
              }} opacity={groupOpacity}>
                    <circle cx={px} cy={py} r={rr + 10} fill="transparent" />
                    <circle cx={px} cy={py} r={rr + (isActive ? 4 : 0)} fill={c} stroke={c} strokeWidth={1.5} />
                    {isActive && <circle cx={px} cy={py} r={rr + 9} fill="none" stroke={c} strokeWidth={1} strokeOpacity={0.5} />}
                    {showLabel && <text x={px} y={py - rr - 5} textAnchor="middle" className="font-mono" fontSize={9} fill="var(--foreground)" opacity={0.9}>
                        {r.region}
                      </text>}
                  </g>;
            })}
            </svg>

            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        <span className="text-muted-foreground/70">FLARE POTENTIAL:</span>
<span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full" style={{
                background: "var(--destructive)"
              }} />β-γ-δ high</span>
<span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full" style={{
                background: "var(--flare-yellow)"
              }} />β-γ</span>
<span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full" style={{
                background: "var(--photon-blue)"
              }} />β</span>
<span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full" style={{
                background: "var(--muted-foreground)"
              }} />α calm</span>
            </div>
            <p className="text-center mt-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground/70">
              Dot size = spot area · brightness = how directly it faces Earth (dim = near the limb)
            </p>

            {/* Desktop blurb — under the disk */}
            <div className="hidden lg:block mt-10">
              <ReadingTheDisk />
            </div>
          </div>

          {/* Side panel — telemetry visual language */}
          <div className="lg:col-span-5">
            <div className="border border-secondary/20 rounded-md p-7 min-h-[320px] flex flex-col" style={{
            backgroundColor: "rgba(15, 17, 21, 0.6)",
            backdropFilter: "blur(24px)"
          }}>
              {status === "loading" && <p className="font-mono text-xs uppercase tracking-widest text-foreground/40 animate-pulse">Acquiring SWPC data…</p>}
              {status === "error" && <p className="font-mono text-xs uppercase tracking-widest text-destructive">SWPC feed unreachable. Retrying on next cycle.</p>}

              {status === "ok" && activeRegion && <div className="flex-1">
                  <div className="flex items-center gap-2 mb-5">
                    <span className="size-1.5 animate-pulse" style={{
                  backgroundColor: GREEN
                }} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/40">REGION DETAIL</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="size-2.5 rounded-full" style={{
                  background: classColor(activeRegion.mag)
                }} />
                    <span className="font-default text-2xl font-bold text-foreground">AR{activeRegion.region}</span>
                  </div>
                  <p className="font-mono text-xs uppercase tracking-widest text-foreground/40 mb-6">{activeRegion.location}</p>
                  <dl className="space-y-3 font-mono text-[11px] uppercase tracking-wider">
                    <div className="flex justify-between"><dt className="text-foreground/40">MAGNETIC</dt><dd className="text-foreground">{activeRegion.mag || "—"}</dd></div>
                    <div className="flex justify-between"><dt className="text-foreground/40">SPOT (ZURICH)</dt><dd className="text-foreground">{activeRegion.spot || "—"}</dd></div>
                    <div className="flex justify-between"><dt className="text-foreground/40">AREA</dt><dd className="text-foreground">{activeRegion.area} MSH</dd></div>
                    <div className="flex justify-between"><dt className="text-foreground/40">SPOTS</dt><dd className="text-foreground">{activeRegion.spots}</dd></div>
                  </dl>
                  <button onClick={() => setActive(null)} className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/40 hover:text-foreground transition-colors">
                    ← BACK TO ALL REGIONS
                  </button>
                </div>}

              {status === "ok" && !activeRegion && <div className="flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-secondary/20">
                    <div className="flex items-center gap-2">
                      <span className="size-1.5 animate-pulse" style={{
                    backgroundColor: GREEN
                  }} />
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/40">REGION INDEX</span>
                    </div>
                    <div className="flex gap-1.5">
                      {SORT_LABELS.map(s => <button key={s.key} onClick={() => setSort(s.key)} className={`font-mono text-[9px] uppercase tracking-wider px-2.5 py-1 rounded border transition-colors ${sort === s.key ? "bg-foreground text-background border-foreground" : "bg-transparent text-foreground/40 border-secondary/20 hover:border-foreground/40"}`}>
                          {s.label}
                        </button>)}
                    </div>
                  </div>
                  <div className="flex-1 max-h-[300px] overflow-y-auto -mx-2 px-2">
                    {sortedRegions.map(r => <button key={r.region} onClick={() => toggle(r.region)} className="w-full flex items-center gap-3 py-2.5 border-b border-secondary/10 last:border-b-0 hover:bg-secondary/5 transition-colors text-left">
                        <span className="size-2.5 rounded-full shrink-0" style={{
                    background: classColor(r.mag)
                  }} />
                        <span className="font-mono text-xs text-foreground w-16">AR{r.region}</span>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/40 w-14">{r.location}</span>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/40 ml-auto">
                          {r.area > 0 ? `${r.area} MSH` : r.mag || "plage"}
                        </span>
                      </button>)}
                  </div>
                </div>}

              {status === "ok" && <p className="mt-6 pt-5 border-t border-secondary/20 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/40">
                  SRS OBSERVED {observedDate || "—"} · SOURCE NOAA SWPC
                </p>}
            </div>
          </div>
        </div>

        {/* Mobile blurb — below the panel */}
        <div className="lg:hidden mt-10">
          <ReadingTheDisk />
        </div>
      </div>
    </section>;
}